import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Switch,
  Chip,
  Tooltip,
  TextField,
  CircularProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AppDispatch } from '../../store/store';
import { getFeatureControlApi } from '../../rtk-apis/clinics/getFeatureControl';
import { updateFeatureControlApi } from '../../rtk-apis/clinics/updateFeatureControl';
import { cardiacTokens as T } from '../../theme/cardiacTheme';

/* ════════════════════════════════════════════════════════════════════════
   Feature control — which optional products each clinic may use.

   The full DICOM viewer, the measurement module and auto distribution. The
   first two were previously all-or-nothing build-time constants, so selling the
   viewer to one clinic and not another meant a separate frontend build. This screen owns
   the per-clinic decision; the backend enforces it.

   What the toggles do NOT touch: Express lite. A clinic without the DICOM
   viewer still opens studies in Express, reviews clips and writes reports —
   that is the entire reason for being able to switch the viewer off.
   ════════════════════════════════════════════════════════════════════════ */

type FeatureKey = 'dicomViewer' | 'measurement' | 'autoDistribution' | 'senderCopy';

type DistributionFormat = 'pdf' | 'txt' | 'rtf';

interface AutoDistributionSettings {
  /** Fixed recipient (e.g. RMI's routing ID). Empty = read the referrer from each report. */
  recipientId: string;
  format: DistributionFormat;
}

interface FeatureRow {
  clinicId: string;
  name: string;
  email: string;
  features: Record<FeatureKey, boolean>;
  autoDistribution?: AutoDistributionSettings;
  updatedAt: string | null;
}

const FORMAT_LABELS: Record<DistributionFormat, string> = {
  pdf: 'PDF',
  txt: 'Text (TXT)',
  rtf: 'RTF',
};

const FEATURES: Array<{
  key: FeatureKey;
  label: string;
  description: string;
}> = [
  {
    key: 'dicomViewer',
    label: 'DICOM Viewer',
    description:
      'The full OHIF viewer. When off, the "Open in viewer" action, the Dicom button and the report DICOM tab disappear, and /viewer is refused. Express lite stays available.',
  },
  {
    key: 'measurement',
    label: 'Measurement',
    description:
      'The measurement workspace, worksheet and Echo Measurements / Wall Motion tabs. When off, /measurement is refused and the measurement endpoints reject the clinic server-side.',
  },
  {
    key: 'autoDistribution',
    label: 'Auto distribution',
    description:
      'Drafts a Medical-Objects distribution for every new report, so the doctor only presses Send after finalising. Goes to a fixed number if one is set, otherwise to the provider number in the report\'s "Referred by" row.',
  },
  {
    key: 'senderCopy',
    label: 'Copy to sender',
    description:
      'Every Medical-Objects send also sends a copy to the sending (From) doctor. The copy is not shown in the distribution list.',
  },
];

const cardSx = {
  background: `linear-gradient(180deg, rgba(20,36,57,0.62), rgba(14,28,46,0.55))`,
  border: `1px solid ${T.line}`,
  borderRadius: '16px',
  p: '18px 20px',
  minWidth: 0,
};

const GRID_COLUMNS = '2fr 1fr 1fr 1.5fr 1fr 1.1fr';

const fmtWhen = (iso: string | null) => {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

interface Props {
  notify?: (msg: string, sev: 'success' | 'error') => void;
}

const FeatureControlTable: React.FC<Props> = ({ notify }) => {
  const dispatch = useDispatch<AppDispatch>();
  /**
   * The parent passes a fresh arrow every render. Held in a ref so that `load`
   * below does not change identity every render — as a useCallback dependency it
   * would re-run the effect that calls it, i.e. fetch in a loop.
   */
  const notifyRef = useRef(notify);
  notifyRef.current = notify;
  const [rows, setRows] = useState<FeatureRow[]>([]);
  const [busy, setBusy] = useState(false);
  /** clinicId:featureKey currently in flight — the switch it belongs to is disabled. */
  const [saving, setSaving] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /**
   * Turning a feature OFF is confirmed; turning it ON is not.
   *
   * Switching the viewer off mid-clinic-day removes a tool clinicians are using,
   * and a mis-click on a dense table of switches is easy. Enabling something is
   * harmless and reversible, so it stays a single click.
   */
  const [confirming, setConfirming] = useState<{ row: FeatureRow; key: FeatureKey } | null>(null);
  /**
   * Auto distribution settings dialog. Opens when the feature is switched on (it needs
   * its settings first) and from the edit button while it is on.
   */
  const [autoEditing, setAutoEditing] = useState<{ row: FeatureRow; enabling: boolean } | null>(
    null
  );
  const [autoForm, setAutoForm] = useState<AutoDistributionSettings>({
    recipientId: '',
    format: 'pdf',
  });
  const autoRecipientError =
    autoForm.recipientId.trim() !== '' && !/^[A-Za-z0-9]{1,20}$/.test(autoForm.recipientId.trim());

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res: any = await dispatch(getFeatureControlApi()).unwrap();
      const clinics = res?.result?.clinics;
      setRows(Array.isArray(clinics) ? clinics : []);
    } catch (err) {
      console.error('[featureControl] fetch failed:', err);
      notifyRef.current?.('Could not load feature control.', 'error');
    } finally {
      setBusy(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const apply = async (
    row: FeatureRow,
    key: FeatureKey,
    next: boolean,
    autoDistribution?: AutoDistributionSettings
  ) => {
    setSaving(`${row.clinicId}:${key}`);
    // Optimistic: a switch that does not move on click reads as broken. Rolled
    // back below if the server refuses.
    setRows(prev =>
      prev.map(r =>
        r.clinicId === row.clinicId ? { ...r, features: { ...r.features, [key]: next } } : r
      )
    );
    try {
      const res: any = await dispatch(
        // Only the flag that moved is sent; the server merges it onto the clinic's
        // current flags so the other switch is left alone.
        updateFeatureControlApi({
          clinicId: row.clinicId,
          features: { [key]: next },
          ...(autoDistribution ? { autoDistribution } : {}),
        })
      ).unwrap();
      const confirmed = res?.result?.features;
      const confirmedAuto = res?.result?.autoDistribution;
      if (confirmed) {
        setRows(prev =>
          prev.map(r =>
            r.clinicId === row.clinicId
              ? { ...r, features: confirmed, autoDistribution: confirmedAuto || r.autoDistribution }
              : r
          )
        );
      }
      const label = FEATURES.find(f => f.key === key)?.label || key;
      notify?.(`${label} ${next ? 'enabled' : 'disabled'} for ${row.name}.`, 'success');
    } catch (err: any) {
      setRows(prev =>
        prev.map(r =>
          r.clinicId === row.clinicId ? { ...r, features: { ...r.features, [key]: !next } } : r
        )
      );
      notify?.(typeof err === 'string' ? err : 'Could not save that change.', 'error');
    } finally {
      setSaving(null);
    }
  };

  const openAutoSettings = (row: FeatureRow, enabling: boolean) => {
    setAutoForm({
      recipientId: row.autoDistribution?.recipientId || '',
      format: row.autoDistribution?.format || 'pdf',
    });
    setAutoEditing({ row, enabling });
  };

  const saveAutoSettings = async () => {
    if (!autoEditing || autoRecipientError) {
      return;
    }
    const { row, enabling } = autoEditing;
    setAutoEditing(null);
    const settings = {
      recipientId: autoForm.recipientId.trim().toUpperCase(),
      format: autoForm.format,
    };
    if (enabling) {
      apply(row, 'autoDistribution', true, settings);
      return;
    }
    setSaving(`${row.clinicId}:autoDistribution`);
    try {
      const res: any = await dispatch(
        updateFeatureControlApi({
          clinicId: row.clinicId,
          features: {},
          autoDistribution: settings,
        })
      ).unwrap();
      const confirmedAuto = res?.result?.autoDistribution;
      setRows(prev =>
        prev.map(r =>
          r.clinicId === row.clinicId ? { ...r, autoDistribution: confirmedAuto || settings } : r
        )
      );
      notify?.(`Auto distribution settings saved for ${row.name}.`, 'success');
    } catch (err: any) {
      notify?.(typeof err === 'string' ? err : 'Could not save that change.', 'error');
    } finally {
      setSaving(null);
    }
  };

  const onToggle = (row: FeatureRow, key: FeatureKey, next: boolean) => {
    if (next) {
      if (key === 'autoDistribution') {
        openAutoSettings(row, true);
        return;
      }
      apply(row, key, true);
      return;
    }
    setConfirming({ row, key });
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return rows;
    }
    return rows.filter(
      r =>
        (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const counts = useMemo(() => {
    const out: Record<FeatureKey, number> = {
      dicomViewer: 0,
      measurement: 0,
      autoDistribution: 0,
      senderCopy: 0,
    };
    rows.forEach(r => {
      FEATURES.forEach(f => {
        if (r.features?.[f.key]) {
          out[f.key] += 1;
        }
      });
    });
    return out;
  }, [rows]);

  const confirmingLabel = confirming
    ? FEATURES.find(f => f.key === confirming.key)?.label || confirming.key
    : '';

  return (
    <Box>
      <Box sx={{ ...cardSx, p: '18px 20px 8px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: '14px',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
              Clinics ({rows.length})
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.ink3, mt: '2px' }}>
              {counts.dicomViewer} with the viewer · {counts.measurement} with measurement ·{' '}
              {counts.autoDistribution} with auto distribution · {counts.senderCopy} with copy
              to sender
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search clinic"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: T.ink3 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 240,
              '& .MuiOutlinedInput-root': { color: T.ink, fontSize: 13 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
            }}
          />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID_COLUMNS,
                gap: '12px',
                pb: '8px',
                borderBottom: `1px solid ${T.line}`,
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: T.ink3,
              }}
            >
              <span>Clinic</span>
              {FEATURES.map(f => (
                <Tooltip
                  key={f.key}
                  title={f.description}
                  arrow
                >
                  <span style={{ cursor: 'help' }}>{f.label}</span>
                </Tooltip>
              ))}
              <span>Last changed</span>
            </Box>

            {busy && !rows.length && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: '28px' }}>
                <CircularProgress size={22} sx={{ color: T.teal }} />
              </Box>
            )}

            {!busy && !filtered.length && (
              <Typography sx={{ fontSize: 13, color: T.ink3, py: '24px', textAlign: 'center' }}>
                {rows.length ? 'No clinic matches that search.' : 'No clinics found.'}
              </Typography>
            )}

            {filtered.map(r => (
              <Box
                key={r.clinicId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLUMNS,
                  gap: '12px',
                  alignItems: 'center',
                  py: '11px',
                  borderBottom: `1px solid ${T.line}`,
                  fontSize: 13.5,
                  color: T.ink,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#fff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.name}
                  </Typography>
                  {!!r.email && (
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: T.ink3,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {r.email}
                    </Typography>
                  )}
                </Box>

                {FEATURES.map(f => {
                  const on = !!r.features?.[f.key];
                  const inFlight = saving === `${r.clinicId}:${f.key}`;
                  const showAuto = f.key === 'autoDistribution' && on;
                  return (
                    <Box
                      key={f.key}
                      sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}
                    >
                      <Switch
                        checked={on}
                        disabled={inFlight}
                        onChange={e => onToggle(r, f.key, e.target.checked)}
                        size="small"
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: T.teal },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: T.teal2,
                          },
                        }}
                      />
                      <Chip
                        size="small"
                        label={on ? 'On' : 'Off'}
                        sx={{
                          height: 18,
                          fontSize: 10,
                          color: on ? T.teal : T.ink3,
                          background: on ? 'rgba(45,212,191,0.12)' : 'rgba(106,126,156,0.12)',
                          border: `1px solid ${on ? 'rgba(45,212,191,0.4)' : T.line}`,
                        }}
                      />
                      {showAuto && (
                        <>
                          <Tooltip
                            title={
                              r.autoDistribution?.recipientId
                                ? 'Every new report is drafted to this number.'
                                : 'Drafted once the report\'s "Referred by" row has a provider number.'
                            }
                            arrow
                          >
                            <Typography
                              sx={{
                                fontSize: 11.5,
                                color: T.ink2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                              }}
                            >
                              {r.autoDistribution?.recipientId || 'Referrer'} ·{' '}
                              {(r.autoDistribution?.format || 'pdf').toUpperCase()}
                            </Typography>
                          </Tooltip>
                          <IconButton
                            size="small"
                            aria-label="Edit auto distribution settings"
                            disabled={inFlight}
                            onClick={() => openAutoSettings(r, false)}
                            sx={{ color: T.ink3, p: '2px' }}
                          >
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  );
                })}

                <Typography sx={{ fontSize: 12.5, color: T.ink3 }}>
                  {fmtWhen(r.updatedAt)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 12, color: T.ink3, mt: '12px', lineHeight: 1.6 }}>
        A change takes effect for a clinician on their next page load. The server enforces
        these flags immediately — a disabled feature cannot be reached by typing its URL.
        Express lite is never affected.
      </Typography>

      <Dialog
        open={!!confirming}
        onClose={() => setConfirming(null)}
        PaperProps={{ sx: { background: T.surface, border: `1px solid ${T.line}` } }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
          Turn off {confirmingLabel}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: T.ink2, fontSize: 13.5 }}>
            {confirming?.row.name} will lose access to {confirmingLabel.toLowerCase()} on their
            next page load, and the server will refuse it straight away — including anyone
            currently on that screen.
            {confirming?.key === 'dicomViewer'
              ? ' Express lite is unaffected, so they can still review clips and write reports.'
              : confirming?.key === 'autoDistribution'
                ? ' New reports will no longer get a draft. Drafts already created are kept.'
                : confirming?.key === 'senderCopy'
                  ? ' Sends will no longer copy the sending doctor.'
                  : ' Measurements already saved are kept, but cannot be opened or edited.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirming(null)}
            sx={{ color: T.ink2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (confirming) {
                apply(confirming.row, confirming.key, false);
              }
              setConfirming(null);
            }}
            sx={{ background: T.red, textTransform: 'none', '&:hover': { background: T.red } }}
          >
            Turn off
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!autoEditing}
        onClose={() => setAutoEditing(null)}
        PaperProps={{ sx: { background: T.surface, border: `1px solid ${T.line}`, width: 440 } }}
      >
        <DialogTitle sx={{ color: '#fff', fontSize: 16, fontWeight: 700 }}>
          Auto distribution — {autoEditing?.row.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: T.ink2, fontSize: 13.5, mb: '16px' }}>
            Each new report gets a distribution draft. Set a fixed number if every report goes to
            the same place (e.g. a routing ID). Leave it empty to use the provider number in the
            report&apos;s &quot;Referred by&quot; row.
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Fixed recipient number (optional)"
            placeholder="e.g. RI48600004V"
            value={autoForm.recipientId}
            onChange={e => setAutoForm(f => ({ ...f, recipientId: e.target.value }))}
            error={autoRecipientError}
            helperText={autoRecipientError ? 'Letters and digits only, up to 20.' : ' '}
            sx={{
              mb: '8px',
              '& .MuiOutlinedInput-root': { color: T.ink },
              '& .MuiInputLabel-root': { color: T.ink3 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
            }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Format"
            value={autoForm.format}
            onChange={e =>
              setAutoForm(f => ({ ...f, format: e.target.value as DistributionFormat }))
            }
            sx={{
              '& .MuiOutlinedInput-root': { color: T.ink },
              '& .MuiInputLabel-root': { color: T.ink3 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: T.line },
            }}
          >
            {(Object.keys(FORMAT_LABELS) as DistributionFormat[]).map(f => (
              <MenuItem
                key={f}
                value={f}
              >
                {FORMAT_LABELS[f]}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAutoEditing(null)}
            sx={{ color: T.ink2, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={autoRecipientError}
            onClick={saveAutoSettings}
            sx={{ background: T.teal2, textTransform: 'none', '&:hover': { background: T.teal2 } }}
          >
            {autoEditing?.enabling ? 'Turn on' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureControlTable;
